import type { ITagItem } from '@tcweb/connect-ui';
import {
  GetConfigurationResponse,
  IssueResponse,
  IssueLinksResponse,
} from '@data-access/connect/tc-issue-management';
import { CheckFollowStatusBulkResponse } from '@data-access/connect/tc-follow';
import { JSXElement } from 'solid-js';
import { IProjectUser, IProjectGroup } from '../utils/util';
import type { IssueIcon } from '../utils/issue-icons';
import {
  CreateViewRequest,
  type Camera,
  type ReservedAppName,
} from '@data-access/connect/tc-tcps';
import type { IExportJobDetail } from '@trimble/tc-issue-management-providers';
/**
 * Link response with issueId for snapshot data.
 * Used in the onLinksSnapshotLoaded callback.
 */
export interface IssueLinkWithIssueId extends IssueLinksResponse {
  readonly issueId: string;
}

// Re-export snackbar types for convenience
export type {
  ConnectSnackbarProps,
  ISnackBarMessage,
  IColors,
} from '@tcweb/connect-ui';
export type { SnackbarProviderProps } from '../components/ConnectSnackbar';

export type IRightPanelViewMode =
  | 'displayCreatePanel'
  | 'displayEditPanel'
  | 'displayDetailsPanel';

/**
 * Identifiers for UI elements that can be hidden via {@link IFeatureConfig.hiddenElements}.
 * All elements are visible by default; listing an ID hides that element.
 */
export type HideableElement =
  | 'references-tab'
  | 'marker-tab'
  | 'linked-model-objects'
  | 'view-3d-button'
  | 'load-original-versions-option';

/**
 * Consumer-provided text overrides for UI labels.
 * All keys are optional — omitted keys fall back to built-in defaults.
 */
export interface ICustomText {
  /** @default "View 3D" */
  readonly view3DButtonLabel?: string;
  /** @default "View in 3D viewer" */
  readonly viewIn3DViewerLabel?: string;
}

/**
 * Feature configuration for the Marker / FOR3D tab.
 */
export interface IMarkerFeatureConfig {
  markerIcon?: string;
}

/**
 * Feature configuration for the References / FILE tab.
 */
export interface IReferencesFeatureConfig {
  /**
   * Resource IDs whose issue links should be hidden from the References UI.
   * Hidden links still exist via the API but are excluded from rendering,
   * counts, and bulk "Remove All" operations — effectively making them
   * undeletable through the UI.
   */
  hiddenResourceIds?: readonly string[];
}

/**
 * Feature configuration for issue view preview in the create panel.
 * When enabled, captures a screenshot + camera on create-mode entry
 * and auto-creates a view after successful issue creation.
 */
export interface IIssueViewPreviewFeatureConfig {
  readonly enabled: boolean;
}

/**
 * Feature configuration for Maya reserved-view routing.
 */
export interface IMayaReservedViewRoutingFeatureConfig {
  /**
   * When false, Maya reserved-view actions use the host `onIssueViewSelect`
   * callback instead of opening ProjectSight directly. Defaults to true so
   * consuming applications without a 360 viewer get direct redirection.
   */
  readonly redirectToProjectSight?: boolean;
}

/**
 * Data returned by the `onCaptureIssueViewPreview` callback.
 * Contains the screenshot and camera state needed to display a preview
 * and create a view after issue creation.
 */
export interface IIssueViewPreviewData {
  readonly imageData: string;
  readonly camera: Camera;
  readonly title: string;
}

/**
 * Top-level feature configuration object passed to the library.
 *
 * Use {@link hiddenElements} to hide specific UI elements by ID.
 * Use sub-config objects ({@link marker}, {@link references}) for
 * non-visibility settings like icons or resource filters.
 *
 * @example
 * // Hide the FOR3D tab and the View 3D button
 * { hiddenElements: ['marker-tab', 'view-3d-button'] }
 *
 * @example
 * // Hide specific resources from the References tab
 * { references: { hiddenResourceIds: ['my-resource'] } }
 */
export interface IFeatureConfig {
  marker?: IMarkerFeatureConfig;
  references?: IReferencesFeatureConfig;
  /**
   * When true, fires `onIssueCreated` callback after every issue creation,
   * even without pending marker data. Allows consumers to create IssueLinks
   * based on the current viewing context without explicit marker placement.
   */
  alwaysLinkOnCreate?: boolean;
  /**
   * Element IDs to hide from the panel UI.
   * Omitted elements default to visible.
   */
  hiddenElements?: readonly HideableElement[];
  /**
   * Opt-in issue view preview in the create panel.
   * Off by default. When enabled, the panel captures a screenshot on entry
   * and auto-creates a view after successful issue creation.
   */
  issueViewPreview?: IIssueViewPreviewFeatureConfig;
  /**
   * Behavior for Maya reserved-view routing.
   * Direct ProjectSight redirect is enabled by default.
   */
  mayaReservedViewRouting?: IMayaReservedViewRoutingFeatureConfig;
  /** Consumer-provided text overrides for UI labels. */
  customText?: ICustomText;
}

/** Display mode for the issue management panel */
export enum IssueManagementMode {
  /** Show the issue list (default) */
  List = 'list',
  /** Open directly to create a new issue */
  Create = 'create',
  /** Open to view a specific existing issue */
  ViewIssue = 'viewIssue',
}

/**
 * Discriminated union for requesting a specific panel display state.
 * Reactive - changes trigger panel navigation.
 */
export type PanelDisplayRequest =
  | { readonly mode: IssueManagementMode.List }
  | {
      readonly mode: IssueManagementMode.Create;
      readonly markerData?: IPendingMarkerData;
    }
  | { readonly mode: IssueManagementMode.ViewIssue; readonly issueId: string };

/**
 * Marker data captured from 3D viewer for issue creation.
 * The `point` field maps directly to the Issue API's marker.point { x, y, z }.
 * The `locationContext` field stores consumer-specific data in IssueLink properties.
 */
export interface IPendingMarkerData {
  /** 3D position where marker was placed - maps to Issue API marker.point */
  readonly point: { x: number; y: number; z: number };
  /** Consumer-defined context data stored in IssueLink properties as JSON */
  readonly locationContext?: Record<string, unknown>;
}

/** Application context provided by consumer for IssueLink creation */
export interface IApplicationContext {
  /** Resource identifier for IssueLinks (e.g., 'maya360') */
  readonly resourceId: string;
  /** Human-readable app name for descriptions */
  readonly appName: string;
}

/** Data returned by consumer's onIssueCreated callback for IssueLink creation */
export interface IIssueCreatedData {
  /** Application context for IssueLink metadata */
  readonly appContext: IApplicationContext;
  /** Array of location contexts to create IssueLinks for */
  readonly contexts: readonly Record<string, unknown>[];
}

export type IssueType = 'Issue' | 'Comment' | 'Request' | 'Solution';
export type IssueTypeIcon = 'error' | 'chat' | 'arrow_upward' | 'check';
export type IssueTypeColor = 'critical' | 'normal' | 'high' | 'low';

export type IssuePriority = 'Critical' | 'High' | 'Normal' | 'Low';
export type IssuePriorityColor = 'critical' | 'high' | 'normal' | 'low';

export enum IssueManagementWidgetMode {
  IssueExport = 'topicexport',
  IssueImport = 'topicimport',
  Upload = 'upload',
}

export enum IssueExchangeStatus {
  Failed = 'FAILED',
  Cancelled = 'CANCELLED',
  Processing = 'PROCESSING',
  Completed = 'COMPLETED',
}

/**
 * An export job that has been registered in the local store.
 * Extends {@link IExportJobDetail} with tracking metadata added by
 * {@link handleAddExports} — a unique list-item id and the batch number
 * that caused this job to be enqueued.
 */
export interface ITrackedExportJob extends IExportJobDetail {
  readonly id: string;
  readonly batchNumber: number;
}
export interface TableColumn {
  id: string;
  accessor: string;
  header: string;
  cellRenderer?(value: any, row: IssueResponse): HTMLElement;
}

/** Configuration for storing reserved (app-specific) data alongside a view */
export interface IssueReservedViewDataConfig {
  /** The app identifier sent as the ?app= query parameter */
  readonly appName: ReservedAppName;
  /** Called to get the JSON data to store when creating a view */
  getReservedData(context: {
    viewId: string;
    issueId?: string;
  }): Record<string, unknown> | Promise<Record<string, unknown>>;
  /** Called when issue link creation fails after a successful issue create */
  onIssueLinkError?(issueId: string, error: Error): void;
  /** Called when reserved view data fails to save after a successful view create */
  onReservedDataError?(viewId: string, error: Error): void;
}

export interface TcIssueManagementProps {
  children?: any;
  accessToken: string;
  projectId: string;
  /**
   * Connect location, e.g. `northAmerica`. A location that matches a server
   * limits the lookup to that server; otherwise every region is searched.
   */
  readonly projectLocation?: string;
  environment?: 'int' | 'qa' | 'stage' | 'prod';
  threeDCallbacks?: I3DCallbacks;
  /** Feature visibility configuration. Omitted keys default to enabled. */
  featureConfig?: IFeatureConfig;

  /**
   * Reactive display request for the panel.
   * Changes trigger panel navigation to the requested state.
   */
  displayRequest?: PanelDisplayRequest;

  /** When true, fetches IssueLinks when an issue is selected and includes them in onIssueItemSelect payload */
  prefetchIssueLinksOnSelect?: boolean;

  // Snapshot prefetch options for early data loading
  /** When true, fetches all issues on mount using the snapshot endpoint */
  prefetchAllIssues?: boolean;
  /** When true, fetches all issue links on mount using the snapshot endpoint */
  prefetchAllLinks?: boolean;
  /** Callback fired when issues snapshot data is loaded */
  onIssuesSnapshotLoaded?(issues: readonly IssueResponse[]): void;
  /** Callback fired when links snapshot data is loaded */
  onLinksSnapshotLoaded?(links: readonly IssueLinkWithIssueId[]): void;
  /** Callback fired when the details panel is closed. Use to clear display request state. */
  onPanelClose?(): void;
  /** Widget functionality callbacks for Redux integration (optional, required for widget to show) */
  setWidgetMode?(mode: IssueManagementWidgetMode): void;
  clearExports?(): void;
  addExports?(jobs: IExportJobDetail[]): void;

  /** When true, automatically re-fetches issue and link snapshots after create/update/delete mutations */
  refetchSnapshotsOnMutation?: boolean;

  /** Optional config for saving app-specific reserved data when views are created on issues */
  reservedViewDataConfig?: IssueReservedViewDataConfig;

  /**
   * Controls whether the "Show Topics in 3D" toggle is checked.
   * Only rendered when `threeDCallbacks` is provided.
   * Consumers should maintain this state externally and pass updates via
   * `threeDCallbacks.onShowAllMarkerChange`.
   */
  showAllMarker?: boolean;
  /**
   * File version IDs of loaded 3D models in the host viewer.
   * Forwarded to `CreateViewRequest.models` when auto-creating an issue view
   * from a 3D snapshot preview. Defaults to an empty array when omitted.
   */
  modelIds?: string[];
}

export interface IssueListProps {
  projectId: string;
  contextId: string;
  accessToken?: string;
  onRowClick?(rowData: IssueResponse): void;
  isLoading: boolean;
  hasError: boolean;
  errorInfo: {
    title: string;
    message: string;
    icon: string;
  } | null;
  issuesData: any | null;
  projectUsersData: any | null;
  followStatusData?: CheckFollowStatusBulkResponse | null;
  configurationData?: GetConfigurationResponse;
  /** Normalized list of default + custom icons for type icon rendering */
  iconsList?: readonly IssueIcon[];
}

export interface IssuePanelProps {
  titleSectionRight: JSXElement;
  isSearchMode: boolean;
  title?: string;
  onAddIssue?(): void;
  onSort?(option: string): void;
  isSortOrderDesc?: boolean;
  onFilter?(option: string): void;
  onSearch?(): void;
  onRefresh?(): void;
  panelContent?: JSXElement;
  configurationData?: GetConfigurationResponse; // Configuration data from API containing _actions array
  /** Whether the "Show Topics in 3D" toggle is checked. Toggle is only rendered when this prop is provided. */
  showAllMarker?: boolean;
  /** Called when the user toggles the "Show Topics in 3D" switch. */
  onChangeMarker?(status: boolean): void;
}

export interface IssueRightPanelProps {
  isOpen: boolean;
  selectedIssue: IssueResponse | null;
  onClose(): void;
  viewPort: IRightPanelViewMode;
  onViewPortChange(viewPort: IRightPanelViewMode): void;
  // Navigation props
  issuesList: IssueResponse[];
  onSelectIssue(issue: IssueResponse): void;
  // Comment-related props
  projectId: string;
  contextId?: string; // Context ID for API calls
  accessToken: string;
  currentUser: IUser;
  project?: any;
  projectUsers?: readonly any[];
  isCommentActionAllowed?(action: string): boolean;
  policyToolTip?: any;
  allAssignees: ITagItem[];
  projectTags: ITagItem[];
  configurationData?: any; // Configuration data from API
  // 3D Action callbacks
  threeDCallbacks?: I3DCallbacks;
  projectUsersData?: readonly IProjectUser[] | null;
  projectGroupsData?: readonly IProjectGroup[] | null;
  followStatusData?: CheckFollowStatusBulkResponse | null; // Follow status data for all issues
  /** Pending marker data for new issue creation */
  pendingMarkerData?: IPendingMarkerData;
  /** Widget functionality callbacks for Redux integration (optional) */
  setWidgetMode?(mode: IssueManagementWidgetMode): void;
  clearExports?(): void;
  addExports?(jobs: IExportJobDetail[]): void;
  /** Feature visibility configuration. Omitted keys default to enabled. */
  featureConfig?: IFeatureConfig;
  /** Optional config for saving app-specific reserved data when views are created on issues */
  reservedViewDataConfig?: IssueReservedViewDataConfig;
  /** Callback fired when an issue is successfully created or updated (used internally for snapshot refetch) */
  onIssueMutated?(issue: IssueResponse): void;
  /** Callback fired when an issue is successfully deleted (used internally for snapshot refetch) */
  onIssueDeleted?(issueId: string): void;
  /** Normalized list of default + custom icons for type icon rendering */
  iconsList?: readonly IssueIcon[];
  /**
   * File version IDs of loaded 3D models in the host viewer.
   * Forwarded to `CreateViewRequest.models` when auto-creating an issue view
   * from a 3D snapshot preview.
   */
  modelIds: string[];
}

// Add comment-related interfaces if not already available
export interface IUser {
  readonly tiduuid: string;
  readonly email: string;
  readonly name: string;
}

export interface ITopicComment {
  readonly guid: string;
  readonly comment: string;
  readonly author: string;
  readonly date: string;
  readonly viewpoint?: any;
  readonly attachments?: readonly any[];
}

// 3D Action Types
export type I3DActionType =
  | 'add-issue-marker'
  | 'fit-issue-marker'
  | 'view-issue-model-objects'
  | 'link-issue-model-objects'
  | 'unlink-issue-model-objects'
  | 'unlink-all-issue-model-objects'
  | 'add-issue-view'
  | 'issue-item-select'
  | 'issue-created'
  | 'remove-issue-marker'
  | 'issue-view-select'
  | 'add-issue-comment-view'
  | 'load-viewpoint-original-versions';

export interface I3DActionRequest {
  readonly type: I3DActionType;
  readonly issueId: string;
  readonly contextId?: string;
  readonly payload?: any;
}

export interface I3DActionResponse {
  readonly type: I3DActionType;
  readonly success: boolean;
  readonly message?: string;
  readonly data?: any;
}

export interface IMarker {
  readonly id: string;
  readonly position: {
    readonly x: number;
    readonly y: number;
    readonly z: number;
  };
  readonly issueId: string;
  readonly contextId?: string;
  readonly createdAt: Date;
}

/**
 * Prepared marker data for a single issue, passed to the host via
 * {@link I3DCallbacks.onMarkersChange}. Contains all info the host needs
 * to render markers without querying the Issues API again.
 */
export interface IssueMarkerInfo {
  readonly issueId: string;
  readonly point: {
    readonly x: number;
    readonly y: number;
    readonly z: number;
  };
  readonly type?: string;
  readonly priority?: string;
  readonly status?: string;
}

export interface I3DCallbacks {
  onAddIssueMarker?(request: I3DActionRequest): Promise<I3DActionResponse>;
  onFitIssueMarker?(request: I3DActionRequest): Promise<I3DActionResponse>;
  onViewIssueModelObjects?(
    request: I3DActionRequest
  ): Promise<I3DActionResponse>;
  onLinkIssueModelObjects?(
    request: I3DActionRequest
  ): Promise<I3DActionResponse>;
  onUnlinkIssueModelObjects?(
    request: I3DActionRequest
  ): Promise<I3DActionResponse>;
  onUnlinkAllIssueModelObjects?(
    request: I3DActionRequest
  ): Promise<I3DActionResponse>;
  onAddIssueView?(
    request: Omit<I3DActionRequest, 'payload'>
  ): Promise<
    Omit<I3DActionResponse, 'data'> & { data: CreateViewRequest | null }
  >;
  onIssueItemSelect?(
    request: Omit<I3DActionRequest, 'payload'> & {
      payload?: {
        issue: IssueResponse;
        issueLinks?: IReference[];
      };
    }
  ): Promise<I3DActionResponse>;
  onRemoveIssueMarker?(request: I3DActionRequest): Promise<I3DActionResponse>;
  onIssueViewSelect?(
    request: Omit<I3DActionRequest, 'payload'> & {
      payload?: {
        viewId: string;
        /** Reserved view data fetched from the Views API, if reservedViewDataConfig is provided. */
        reservedViewData?: Record<string, unknown>;
      };
    }
  ): Promise<I3DActionResponse>;
  onAddIssueCommentView?(
    request: I3DActionRequest
  ): Promise<
    Omit<I3DActionResponse, 'data'> & { data: CreateViewRequest | null }
  >;
  onIssueCreated?(request: I3DActionRequest): Promise<I3DActionResponse>;
  onLoadViewpointOriginalVersions?(
    request: I3DActionRequest
  ): Promise<I3DActionResponse>;
  /** Capture a view preview (screenshot + camera) for the create issue panel. */
  onCaptureIssueViewPreview?(): Promise<IIssueViewPreviewData | null>;

  /** Fired when an issue is successfully created */
  onIssueCreatedSuccess?(issue: IssueResponse): void;
  /** Fired when an issue is successfully updated */
  onIssueUpdatedSuccess?(issue: IssueResponse): void;
  /** Fired when issue creation or update fails */
  onIssueCreateOrUpdateError?(error: Error): void;
  /** Fired when an issue is successfully deleted */
  onIssueDeletedSuccess?(issueId: string): void;
  /** Fired when issue deletion fails */
  onIssueDeleteError?(issueId: string, error: Error): void;
  /** Fired when a view is successfully added to an issue */
  onIssueViewCreatedSuccess?(viewId: string, issueId: string): void;
  /** Fired when a view is successfully created and attached to a comment */
  onCommentViewCreatedSuccess?(
    viewId: string,
    issueId: string,
    commentId?: string
  ): void;
  /**
   * Fired when a captured comment view is abandoned or its create fails before
   * reserved data is saved. Hosts should drop the matching pending snapshot.
   */
  onCommentViewCaptureCancelled?(issueId: string): void;
  /** Fired when adding a view to an issue fails */
  onIssueViewCreateError?(issueId: string, error: Error): void;
  /** Fired when a view is successfully removed from an issue */
  onIssueViewDeletedSuccess?(viewId: string): void;
  /** Fired when removing a view from an issue fails */
  onIssueViewDeleteError?(viewId: string, error: Error): void;
  /**
   * Called when the user toggles the "Show Topics in 3D" switch.
   * Consumers should update their 3D viewer marker visibility accordingly.
   */
  onShowAllMarkerChange?(showAll: boolean): void;

  /**
   * Called whenever the visible set of issue markers changes.
   * This fires:
   * - After the issues list is loaded/filtered/searched
   * - When the "Show Topics in 3D" toggle state changes
   *
   * The provided marker list is authoritative. An empty array means the host
   * should hide topic markers.
   * Consumers can use this to add/remove markers in the 3D viewer.
   */
  onMarkersChange?(
    markers: readonly IssueMarkerInfo[],
    showAll?: boolean
  ): void;

  /**
   * Called when the user selects "Locate in Explorer" for a FILE reference.
   * Consumers should reveal the file's location in their file explorer/tree.
   */
  onLocateReferenceInExplorer?(reference: {
    resourceId: string;
    versionId?: string;
  }): void;
}

export interface SearchInputProps {
  placeholder?: string;
  value?: string;
  onChange?(value: string): void;
  className?: string;
}

export interface BadgeProps {
  type: 'type' | 'priority' | 'status';
  value: string;
  color: string;
  icon?: string;
  className?: string;
}

export interface DropdownProps {
  value: string;
  icon?: string;
  readonly?: boolean;
  className?: string;
  onChange?(value: string): void;
}

export interface AvatarGroupProps {
  users: string[];
  maxVisible?: number;
  size?: 'sm' | 'md' | 'lg';
}

// Reference types for attachments
export type IReferenceType = 'FILE' | 'URL' | 'OBJECT' | 'CLASH';

export interface IReference {
  id?: string;
  url?: string;
  description: string;
  /** type was removed in BCF3.0! It is calculated in the get function so existing code keeps on working. */
  type?: IReferenceType;
  thumbnailUrl?: string;
  name?: string;
  versionId?: string;
  resourceId?: string;
  properties?: Record<string, string>;
}

export type {
  IssueAclMap,
  IssueAccessBucket,
  IPermissionEntry,
} from './issue-permissions.types';
export {
  ISSUE_ACCESS_BUCKETS,
  MAX_ISSUE_ACL_IDENTITIES,
  PERMISSION_CONSTANTS,
} from './issue-permissions.types';
